//@ts-nocheck
/*
  DEPRECATED: Legacy payment schedule calculator.
  TODO (post-flag): Remove this file and its tests once
  'enableNewPaymentPlans' is baseline. Migrate all callers to the new
  calculator in './paymentScheduleNew' (or the renamed canonical
  './paymentSchedule').

  Migration tips:
  - Find usages:
      rg "from './paymentSchedule'" XG-FE/src
      rg "getPaymentSchedule\(" XG-FE/src
  - Update imports/usages to the new API shape returned by the adapter
    or new calculator.
*/

// Utility to compute a booking payment schedule (dates + amounts)
// Inputs are date-only strings (YYYY-MM-DD). Treat them as literal dates without timezone math.

const LABEL_ORDER = {
  deposit: 1,
  second: 2,
  third: 3,
  fourth: 4,
  final: 5,
  full: 6
};

const isValidDateString = (s) =>
  typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const parseYMDToUTC = (s) => {
  // s is YYYY-MM-DD
  const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
  return new Date(Date.UTC(y, m - 1, d));
};

const formatUTCToYMD = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const compareYMD = (a, b) => {
  // Returns negative if a < b, positive if a > b, 0 if equal
  // Safe to compare as strings for YYYY-MM-DD, but use UTC dates to be explicit
  if (!isValidDateString(a) || !isValidDateString(b)) return NaN;
  const da = parseYMDToUTC(a).getTime();
  const db = parseYMDToUTC(b).getTime();
  return da - db;
};

const subtractDays = (ymd, days) => {
  const dt = parseYMDToUTC(ymd);
  dt.setUTCDate(dt.getUTCDate() - Number(days || 0));
  return formatUTCToYMD(dt);
};

const round2 = (n) => {
  const x = Number(n);
  if (!isFinite(x)) return 0;
  return Math.round((x + Number.EPSILON) * 100) / 100;
};

// Parses depositAmount string into a function that computes the deposit value from (total, pax)
// Accepted forms: "20%", "199.99pp", "500pb" (case-insensitive, whitespace tolerant)
const parseDepositFormula = (depositAmount) => {
  if (typeof depositAmount !== 'string') return { type: 'zero', value: 0 };
  const raw = depositAmount.trim().toLowerCase();
  const m = raw.match(/^\s*([0-9]+(?:\.[0-9]+)?)\s*(%|pp|pb)\s*$/i);
  if (!m) return { type: 'zero', value: 0 }; // Treat parse fail as zero
  const amount = parseFloat(m[1]);
  const type = m[2];
  return { type, value: isNaN(amount) ? 0 : amount };
};

const computeDepositAmount = (formula, total, pax) => {
  const amt = Number(formula?.value ?? 0);
  if (!formula || !formula.type) return 0;
  if (amt <= 0) return 0;
  switch (formula.type) {
    case '%':
      return round2((Number(total) || 0) * (amt / 100));
    case 'pp':
      return round2((Number(pax) || 0) * amt);
    case 'pb':
      return round2(amt);
    default:
      return 0;
  }
};

const coerceNumberOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
};

// Public API
// params: {
//   tripStartDate: 'YYYY-MM-DD',
//   bookingCreationDate: 'YYYY-MM-DD',
//   bookingPlans: [{ occupancy: number, pricePerPerson: number, bookingDeadline: 'YYYY-MM-DD' }],
//   paymentPlan: {
//     depositAmount: string,
//     secondPaymentAmount?: number, secondPaymentDue?: number,
//     thirdPaymentAmount?: number, thirdPaymentDue?: number,
//     fourthPaymentAmount?: number, fourthPaymentDue?: number,
//     finalPaymentDue: number,
//     enablePartialPayments: boolean,
//     allowBookingWithoutPayment: boolean
//   }
// }
// returns: { schedule: Array<{ date, amount, label }>, notBookable: boolean, reason?: string }
export const getPaymentSchedule = ({
  tripStartDate,
  bookingCreationDate,
  bookingPlans = [],
  paymentPlan: originalPaymentPlan
}) => {
  // Handle missing or empty payment plan by defaulting to pay in full
  const paymentPlan = originalPaymentPlan || {
    depositAmount: '100%',
    finalPaymentDue: 0,
    enablePartialPayments: false,
    allowBookingWithoutPayment: false
  };

  // If deposit amount is empty or invalid, default to 100%
  if (!paymentPlan.depositAmount || paymentPlan.depositAmount.trim() === '') {
    paymentPlan.depositAmount = '100%';
    paymentPlan.enablePartialPayments = false;
  }
  // Basic validation
  if (
    !isValidDateString(tripStartDate) ||
    !isValidDateString(bookingCreationDate)
  ) {
    return { schedule: [], notBookable: true, reason: 'invalid_input' };
  }
  if (!paymentPlan || typeof paymentPlan.finalPaymentDue === 'undefined') {
    return { schedule: [], notBookable: true, reason: 'invalid_input' };
  }

  // Bookability checks (inclusive)
  if (compareYMD(bookingCreationDate, tripStartDate) > 0) {
    return { schedule: [], notBookable: true, reason: 'after_trip_start' };
  }

  const deadlines = (bookingPlans || [])
    .map((p) => p?.bookingDeadline)
    .filter((d) => isValidDateString(d));
  if (deadlines.length > 0) {
    const earliest = deadlines.sort((a, b) => compareYMD(a, b))[0];
    if (compareYMD(bookingCreationDate, earliest) > 0) {
      return {
        schedule: [],
        notBookable: true,
        reason: 'after_booking_deadline'
      };
    }
  }

  // Totals
  const totalPrice = round2(
    (bookingPlans || []).reduce((acc, p) => {
      const occ = Number(p?.occupancy) || 0;
      const ppp = Number(p?.pricePerPerson ?? p?.priceperperson) || 0;
      return acc + occ * ppp;
    }, 0)
  );
  const paxCount = (bookingPlans || []).reduce(
    (acc, p) => acc + (Number(p?.occupancy) || 0),
    0
  );

  // Full payment only when partial disabled
  if (paymentPlan?.enablePartialPayments === false) {
    const full = round2(totalPrice);
    return {
      schedule:
        full === 0
          ? []
          : [{ date: bookingCreationDate, amount: full, label: 'full' }],
      notBookable: false
    };
  }

  // Deposit
  const depositFormula = parseDepositFormula(paymentPlan?.depositAmount);
  const depositAmount = round2(
    computeDepositAmount(depositFormula, totalPrice, paxCount)
  );

  const schedule = [];
  if (depositAmount > 0) {
    schedule.push({
      date: bookingCreationDate,
      amount: depositAmount,
      label: 'deposit'
    });
  }

  // Interim payments (2nd/3rd/4th) — base is TotalPrice - Deposit (do not subtract prior interims)
  const base = Math.max(round2(totalPrice - depositAmount), 0);

  const secondPct = coerceNumberOrNull(paymentPlan?.secondPaymentAmount);
  const secondDue = coerceNumberOrNull(paymentPlan?.secondPaymentDue);
  if (secondPct !== null && secondDue !== null) {
    const amt = round2(base * (secondPct / 100));
    if (amt > 0)
      schedule.push({
        date: subtractDays(tripStartDate, secondDue),
        amount: amt,
        label: 'second'
      });
  }

  const thirdPct = coerceNumberOrNull(paymentPlan?.thirdPaymentAmount);
  const thirdDue = coerceNumberOrNull(paymentPlan?.thirdPaymentDue);
  if (thirdPct !== null && thirdDue !== null) {
    const amt = round2(base * (thirdPct / 100));
    if (amt > 0)
      schedule.push({
        date: subtractDays(tripStartDate, thirdDue),
        amount: amt,
        label: 'third'
      });
  }

  const fourthPct = coerceNumberOrNull(paymentPlan?.fourthPaymentAmount);
  const fourthDue = coerceNumberOrNull(paymentPlan?.fourthPaymentDue);
  if (fourthPct !== null && fourthDue !== null) {
    const amt = round2(base * (fourthPct / 100));
    if (amt > 0)
      schedule.push({
        date: subtractDays(tripStartDate, fourthDue),
        amount: amt,
        label: 'fourth'
      });
  }

  // Final
  const finalDueDays = Number(paymentPlan?.finalPaymentDue) || 0;
  const sumPrev = round2(schedule.reduce((acc, s) => acc + s.amount, 0));
  const finalAmt = Math.max(round2(totalPrice - sumPrev), 0);
  if (finalAmt > 0) {
    schedule.push({
      date: subtractDays(tripStartDate, finalDueDays),
      amount: finalAmt,
      label: 'final'
    });
  }

  // Sort by date asc; same-day labels ordered consistently
  schedule.sort((a, b) => {
    const c = compareYMD(a.date, b.date);
    if (isNaN(c) || c === 0) {
      return (LABEL_ORDER[a.label] || 99) - (LABEL_ORDER[b.label] || 99);
    }
    return c;
  });

  return { schedule, notBookable: false };
};

export default getPaymentSchedule;
